import React, { useEffect, useState, useContext, useCallback } from "react";
import { ALERT_LEVEL } from "../../../constants";
import useApi from "../../../utils/restUtil";
import { alert } from "../../common/Alert";
import { hideSpinner, showSpinner } from "../../common/spinner/Spinner";
import styles from "./ReportBuilder.module.css";
import Section from "./sections/section/Section";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../AppContext";
import { useHistory } from "react-router";
import { saveAs } from "file-saver";
import { Types } from "../../formManagement/formBuilder/components/Types";

// 오프라인 기능 imports
import networkMonitor, {
  shouldUseOfflineMode,
  getNetworkQualityText,
  addNetworkListener,
  removeNetworkListener
} from "../../../utils/networkMonitor";
import { saveReportOffline, deleteOfflineReport } from "../../../utils/offlineStorage";
import { autoCompressIfNeeded } from "../../../utils/imageCompression";
import { shouldUseOfflineMode as realtimeSpeedCheck } from "../../../utils/speedTest";

// 동기화 시스템 imports
import OfflineIndicator from "../../offline/OfflineIndicator";
import UploadProgressOverlay from "../../common/uploadProgress/UploadProgressOverlay";
import { addSyncListener, removeSyncListener } from "../../../utils/backgroundSync";

const ReportBuilder = () => {
  const { t } = useTranslation();
  const { post, put, get, del } = useApi();
  const {
    auth: { user },
    auth,
  } = useContext(AppContext);
  const history = useHistory();

  const [businessCategories, setBusinessCategories] = useState([]);
  const [formCategories, setFormCategories] = useState([]);
  const [forms, setForms] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  const [selectedBusinessCategory, setSelectedBusinessCategory] = useState();
  const [selectedFormCategory, setSelectedFormCategory] = useState();
  const [selectedForm, setSelectedForm] = useState();

  const [report, setReport] = useState({
    status: "draft",
    name: "",
    items: [],
    form: {},
    organization: "",
  });

  // 오프라인 기능 관련 상태
  const [networkState, setNetworkState] = useState({
    isOnline: true,
    shouldUseOfflineMode: false,
    networkQuality: 'unknown'
  });
  const [offlineNotification, setOfflineNotification] = useState(null);

  // 업로드 진행률 상태
  const [uploadProgress, setUploadProgress] = useState({
    isUploading: false,
    total: 0,
    uploaded: 0,
    currentFile: null
  });


  /**
   * 동적 타임아웃 계산 함수
   * - 이미지 개수 및 파일 크기 기반으로 타임아웃 동적 계산
   * - 압축 시간 + 업로드 시간 + 안전 마진 고려
   */
  const calculateDynamicTimeout = useCallback((reportData) => {
    let imageCount = 0;
    let totalImageSize = 0;

    // 업로드 대기 중인 이미지 개수 및 크기 계산
    reportData.items?.forEach(section => {
      section.items?.forEach(field => {
        if (field.type === Types.IMAGE && field.answer?.file && !field.answer.isUploaded) {
          imageCount++;
          totalImageSize += field.answer.file.size || 0;
        }
      });
    });

    // 이미지가 없으면 기본 타임아웃 (30초)
    if (imageCount === 0) {
      console.log('[ReportBuilder] 이미지 없음, 기본 타임아웃: 30초');
      return 30000;
    }

    // 🔥 버그 수정: 압축 시간을 훨씬 여유롭게 계산
    // 1. 압축 시간: 이미지당 평균 5초 (PC 기준, 모바일은 더 오래 걸릴 수 있음)
    // - 고해상도 이미지는 10초 이상 걸릴 수도 있음
    const compressionTime = imageCount * 5000;

    // 2. 업로드 시간 계산
    // - 보수적인 업로드 속도: 3Mbps (느린 환경 고려)
    // - 압축 후 예상 크기: 원본의 40% (평균 압축률 60% 가정)
    const uploadSpeedMbps = 3;
    const estimatedCompressedSize = totalImageSize * 0.4;
    const uploadTimeSec = (estimatedCompressedSize / (1024 * 1024)) / uploadSpeedMbps * 8;
    const uploadTime = uploadTimeSec * 1000;

    // 3. 메타데이터 전송 + 안전 마진 (60초로 증가)
    const safetyMargin = 60000;

    // 총 타임아웃 계산
    const totalTimeout = compressionTime + uploadTime + safetyMargin;

    // 최대 타임아웃: 10분 (600,000ms)
    const finalTimeout = Math.min(totalTimeout, 600000);

    console.log(`[ReportBuilder] 📊 동적 타임아웃 계산:
  - 이미지 개수: ${imageCount}장
  - 총 원본 크기: ${(totalImageSize / (1024 * 1024)).toFixed(2)}MB
  - 예상 압축 후 크기: ${(estimatedCompressedSize / (1024 * 1024)).toFixed(2)}MB
  - 압축 예상 시간: ${(compressionTime / 1000).toFixed(0)}초
  - 업로드 예상 시간: ${(uploadTime / 1000).toFixed(0)}초 (3Mbps 기준)
  - 안전 마진: ${(safetyMargin / 1000).toFixed(0)}초
  - 총 타임아웃: ${(finalTimeout / 1000).toFixed(0)}초 (${(finalTimeout / 60000).toFixed(1)}분)`);

    return finalTimeout;
  }, []);

  const uploadFiles = useCallback(async (signal = null) => {
    console.log('🔍 [uploadFiles] 시작 - 현재 report.items 상태:', JSON.stringify(
      report.items.map(s => s.items.map(f => ({
        type: f.type,
        hasFile: !!f.answer?.file,
        isUploaded: f.answer?.isUploaded,
        name: f.answer?.name
      })))
    ));

    // 총 업로드할 이미지 개수 계산
    let totalImages = 0;
    report.items.forEach(section => {
      section.items?.forEach(field => {
        if (field.type === Types.IMAGE && field.answer?.file && !field.answer.isUploaded) {
          totalImages++;
        }
      });
    });

    console.log(`🔍 [uploadFiles] 업로드할 이미지 개수: ${totalImages}`);

    // 🔥 진행률 초기화 + 렌더링 완료 대기 (모바일 환경 대응)
    if (totalImages > 0) {
      setUploadProgress({
        isUploading: true,
        total: totalImages,
        uploaded: 0,
        currentFile: null
      });

      // 🔥 React 렌더링이 완료될 때까지 약간의 지연 추가
      // 모바일 환경에서는 렌더링이 느려서 프로그레스바가 표시되기 전에
      // 이미지 업로드가 시작될 수 있음 (메인 스레드 블로킹 방지)
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    let uploadedCount = 0;

    // 🔥 에러 발생시 프로그레스바 정리를 위한 try-finally
    try {

    for (const [sectionIdx, section] of report.items.entries()) {
      for (const [fieldIdx, field] of section.items.entries()) {
        if (field.type === Types.IMAGE && field.answer && field.answer.file && !field.answer.isUploaded) {
          console.log(`🔍 [uploadFiles] 이미지 업로드 중: section ${sectionIdx}, field ${fieldIdx}, file: ${field.answer.file.name}`);

          // 🔥 현재 파일 정보 업데이트 + 렌더링 대기
          setUploadProgress(prev => ({
            ...prev,
            currentFile: field.answer.file.name
          }));

          // 🔥 모바일 환경에서 UI 업데이트가 화면에 반영될 시간 확보
          await new Promise(resolve => setTimeout(resolve, 50));

          // 🆕 이미지 압축 (필요한 경우만)
          console.log(`[ReportBuilder] 이미지 압축 확인: ${field.answer.file.name}`);
          const compressedFile = await autoCompressIfNeeded(field.answer.file);

          const formData = new FormData();
          formData.append("file", compressedFile);

          // 🆕 signal을 config로 전달 (취소 가능하도록)
          const config = signal ? { signal } : {};
          const resp = await post("/reports/attachments", formData, config);

          if (resp && resp.data) {
            console.log(`🔍 [uploadFiles] 이미지 업로드 성공: ${resp.data.name}`);

            // 🔥 서버가 리사이즈 완료 후 응답하므로(서버 수정), 이 시점에 파일은 서버에 확실히 존재함
            // → 업로드 성공 즉시 isUploaded=true 설정 (이전의 "3초 대기 후 일괄 설정" 핵 제거)
            // 저장 중 타임아웃/중단이 발생해도 이미 올라간 이미지는 정상 상태로 남는다
            report.items[sectionIdx].items[fieldIdx].answer.name = resp.data.name;
            delete report.items[sectionIdx].items[fieldIdx].answer.file;
            report.items[sectionIdx].items[fieldIdx].answer.isUploaded = true;

            console.log(`🔍 [uploadFiles] 이미지 업로드 성공, 파일명 저장 + isUploaded 설정 - section ${sectionIdx}, field ${fieldIdx}`);

            // 🔥 업로드 완료 카운트 증가
            uploadedCount++;

            // 🔥 프로그레스바만 업데이트 (setReport는 호출하지 않음!)
            setUploadProgress(prev => ({
              ...prev,
              uploaded: uploadedCount,
              currentFile: null
            }));
          }
        }
      }
    }

      console.log('🔍 [uploadFiles] 모든 이미지 업로드 완료');

      // 🆕 모든 이미지 업로드 완료 후 한 번만 setReport 호출
      // (서버가 리사이즈 완료 후 응답하도록 수정되어, 미리보기 깨짐 방지용 3초 대기 핵이 불필요해짐)
      setReport({ ...report });
      console.log('🔍 [uploadFiles] setReport 호출 완료');

    } catch (error) {
      // 🔥 에러 발생시 (타임아웃, 취소 등) 프로그레스바 강제 종료
      console.error('[uploadFiles] 이미지 업로드 중 에러 발생:', error);
      throw error; // 에러를 상위로 전파
    } finally {
      // 🔥 정상 완료든 에러든 무조건 프로그레스바 정리
      console.log('🔍 [uploadFiles] finally 블록 - 진행률 리셋');
      setUploadProgress({
        isUploading: false,
        total: 0,
        uploaded: 0,
        currentFile: null
      });
      console.log('🔍 [uploadFiles] 진행률 리셋 완료');
    }
  }, [post, report]);

  // 기존 서버 저장 로직 (🆕 동적 타임아웃 + HTTP 요청 취소)
  const saveReportToServer = useCallback(async () => {
    // 🆕 이미지가 있는지 미리 확인
    let hasImages = false;
    report.items?.forEach(section => {
      section.items?.forEach(field => {
        if (field.type === Types.IMAGE && field.answer?.file && !field.answer.isUploaded) {
          hasImages = true;
        }
      });
    });

    // 🆕 이미지가 없을 때만 스피너 표시 (이미지 있으면 진행률 바 표시)
    if (!hasImages) {
      showSpinner();
    }

    // 🆕 AbortController 생성 (HTTP 요청 취소용)
    const abortController = new AbortController();
    let timeoutId = null;

    try {
      // 🆕 동적 타임아웃 계산 (이미지 개수/크기 기반)
      const timeoutDuration = calculateDynamicTimeout(report);

      // 🆕 동적 타임아웃 후 HTTP 요청 취소 및 타임아웃 에러 발생
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          console.log(`[ReportBuilder] ⏰ ${timeoutDuration/1000}초 타임아웃 발생! HTTP 요청 취소 중...`);
          abortController.abort(); // 🔥 HTTP 요청 취소!
          reject(new Error('SAVE_TIMEOUT'));
        }, timeoutDuration);
      });

      // 전체 저장 과정을 하나의 Promise로
      const savePromise = (async () => {
        console.log('[ReportBuilder] ⏱️ 이미지 업로드 시작...');

        // uploadFiles에 abortController 전달
        await uploadFiles(abortController.signal);

        console.log('[ReportBuilder] ✅ 이미지 업로드 완료');

        // 🆕 이미지 업로드 완료 후 레포트 저장시 스피너 표시
        if (hasImages) {
          showSpinner();
        }

        // 🔥 오프라인 ID 처리: offline_로 시작하는 ID는 서버에 없으므로 POST로 전송
        const isOfflineId = report.id?.startsWith('offline_');
        let http = post;
        let url = "/reports";
        let reportToSend = { ...report };

        if (report.id && !isOfflineId) {
          // 서버 ID가 있으면 PUT (업데이트)
          http = put;
          url = `/reports/${report.id}`;
        } else if (isOfflineId) {
          // 오프라인 ID는 서버에 보내지 않음 (서버가 새 ID 생성)
          delete reportToSend.id;
          console.log('[ReportBuilder] 🔄 오프라인 ID 감지 → POST로 전환 (서버가 새 ID 생성)');
        }

        console.log('[ReportBuilder] ⏱️ 레포트 데이터 전송 시작...');
        console.log('[ReportBuilder] 📋 전송할 레포트 정보:', {
          originalId: report.id,
          isOfflineId,
          method: http === post ? 'POST' : 'PUT',
          url,
          willCreateNewId: isOfflineId
        });

        // axios에 signal 전달 (취소 가능하도록)
        const config = {
          signal: abortController.signal
        };

        const resp = await http(url, reportToSend, config);
        console.log('[ReportBuilder] ✅ 레포트 저장 완료');

        console.log('🔍 [saveReportToServer] 저장 직전 report.items 상태:', JSON.stringify(
          report.items.map(s => s.items.map(f => ({
            type: f.type,
            hasFile: !!f.answer?.file,
            isUploaded: f.answer?.isUploaded,
            name: f.answer?.name
          })))
        ));

        // 🆕 저장 완료 후 항상 화면 갱신 (이미지 표시 포함)
        const oldReportId = report.id; // 🔥 이전 ID 보관 (오프라인 ID일 수 있음)
        const newReportId = resp.data.id; // 🔥 서버가 생성한 새 ID

        if (!report.id || isOfflineId) {
          console.log('🔍 [saveReportToServer] 새 레포트/오프라인 레포트 - setReport 호출 (서버 ID로 교체)');
          setReport({ ...report, id: newReportId });
        } else {
          console.log('🔍 [saveReportToServer] 기존 레포트 - setReport 호출 (화면 갱신)');
          setReport({ ...report });
        }

        console.log('🔍 [saveReportToServer] setReport 호출 완료 - 알림 표시 직전');
        alert(t("Saved successfully"), ALERT_LEVEL.SUCCESS);
        console.log('🔍 [saveReportToServer] 알림 표시 완료');

        // 🔥 서버 저장 성공 시 IndexedDB에서 오프라인 레포트 삭제 (중복 방지)
        if (oldReportId && oldReportId.startsWith('offline_')) {
          try {
            await deleteOfflineReport(oldReportId);
            console.log('[ReportBuilder] 🗑️ 서버 저장 성공 → IndexedDB에서 오프라인 레포트 삭제:', oldReportId);
            console.log('[ReportBuilder] ✅ 새 서버 ID로 교체 완료:', newReportId);
          } catch (error) {
            // IndexedDB에 없을 수도 있으니 에러는 무시
            console.log('[ReportBuilder] IndexedDB 삭제 시도 (없을 수도 있음):', error.message);
          }
        }

        // 🆕 보존(draft)/송신(submitted) 구분 처리
        if (report.status === "submitted") {
          // 송신: 리스트로 이동
          console.log('🔍 [saveReportToServer] 송신 완료 - 리스트로 이동');
          history.goBack();
        } else {
          // 보존: 현재 페이지에 머물기 (작성 내용 유지)
          console.log('🔍 [saveReportToServer] 보존 완료 - 현재 페이지 유지');
        }
        return { success: true, data: resp.data };
      })();

      // 🆕 전체 저장 과정과 타임아웃 중 먼저 완료되는 것 대기
      const result = await Promise.race([savePromise, timeoutPromise]);

      // 정상 완료시 타임아웃 취소
      if (timeoutId) clearTimeout(timeoutId);

      return result;

    } catch (err) {
      // 타임아웃 취소
      if (timeoutId) clearTimeout(timeoutId);

      console.error('[ReportBuilder] 서버 저장 에러:', err);

      // 타임아웃 또는 요청 취소 에러인지 확인
      if (err.message === 'SAVE_TIMEOUT') {
        console.log('[ReportBuilder] ⏰ 타임아웃! 모든 HTTP 요청 취소됨');
      } else if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        console.log('[ReportBuilder] 🚫 HTTP 요청이 취소되었습니다');
        return { success: false, error: new Error('SAVE_TIMEOUT') };
      }

      return { success: false, error: err };
    } finally {
      hideSpinner();
    }
  }, [post, put, report, t, history, uploadFiles, calculateDynamicTimeout]);

  // 오프라인 지원 saveReport 함수
  const saveReport = useCallback(async () => {
    let spinnerShown = false; // 스피너 상태 추적

    try {
      // 🔥 버그 수정: 네트워크 속도 측정 제거 - 바로 서버 저장 시도
      // - 이미지 압축 중에는 메인 스레드가 블로킹되어 Ping 테스트가 부정확함
      // - 타임아웃 메커니즘이 충분히 여유롭게 설정되어 있으므로 먼저 시도
      // - 실패하면 자동으로 오프라인 fallback 처리
      console.log('[ReportBuilder] 서버 저장 시도 (동적 타임아웃)');
      const result = await saveReportToServer();

      // 저장 실패 또는 타임아웃 시 오프라인 fallback
      if (!result.success) {
        const isTimeout = result.error?.message === 'SAVE_TIMEOUT';
        console.log(`[ReportBuilder] 서버 저장 ${isTimeout ? '타임아웃' : '실패'}, 오프라인 저장으로 fallback...`);

        // 🔥 버그 수정: 프로그레스바가 표시 중이면 강제 종료
        setUploadProgress({
          isUploading: false,
          total: 0,
          uploaded: 0,
          currentFile: null
        });

        showSpinner();
        spinnerShown = true;

        // 🔥 중복 저장 방지: 오프라인 저장 후 반환된 ID로 state 업데이트
        const savedReport = await saveReportOffline({
          ...report,
          userId: user.id,
          userName: user.name
        });

        // 🔥 생성된 오프라인 ID를 React state에 반영 (다음 저장시 같은 ID 사용 → 덮어쓰기)
        if (savedReport && savedReport.id) {
          setReport(prev => ({ ...prev, id: savedReport.id }));
          console.log('[ReportBuilder] 오프라인 ID 업데이트:', savedReport.id);
        }

        hideSpinner();
        spinnerShown = false;

        const message = isTimeout
          ? t("Network timeout. Saved offline instead.")
          : t("Server save failed. Saved offline instead.");

        alert(message, ALERT_LEVEL.WARNING);
        setOfflineNotification({
          type: isTimeout ? 'timeout' : 'fallback',
          message: t("Saved offline due to network issue"),
          timestamp: new Date()
        });

        setTimeout(() => setOfflineNotification(null), 8000);

        return { success: true, offline: true, fallback: true };
      }

      return result;

    } catch (error) {
      console.error('[ReportBuilder] 저장 중 예상치 못한 에러:', error);

      // 예상치 못한 에러 발생시에도 오프라인 저장 시도
      try {
        // 🔥 버그 수정: 프로그레스바가 표시 중이면 강제 종료
        setUploadProgress({
          isUploading: false,
          total: 0,
          uploaded: 0,
          currentFile: null
        });

        if (!spinnerShown) {
          showSpinner();
          spinnerShown = true;
        }

        // 🔥 중복 저장 방지: 오프라인 저장 후 반환된 ID로 state 업데이트
        const savedReport = await saveReportOffline({
          ...report,
          userId: user.id,
          userName: user.name
        });

        // 🔥 생성된 오프라인 ID를 React state에 반영 (다음 저장시 같은 ID 사용 → 덮어쓰기)
        if (savedReport && savedReport.id) {
          setReport(prev => ({ ...prev, id: savedReport.id }));
          console.log('[ReportBuilder] 오프라인 ID 업데이트 (예외처리):', savedReport.id);
        }

        hideSpinner();
        spinnerShown = false;

        alert(t("Network timeout. Saved offline instead."), ALERT_LEVEL.WARNING);
        setOfflineNotification({
          type: 'error',
          message: t("Saved offline due to error"),
          timestamp: new Date()
        });

        setTimeout(() => setOfflineNotification(null), 8000);

        return { success: true, offline: true, error: true };

      } catch (offlineError) {
        console.error('[ReportBuilder] 오프라인 저장도 실패:', offlineError);

        // 🔥 버그 수정: 오프라인 저장 실패시에도 프로그레스바 강제 종료
        setUploadProgress({
          isUploading: false,
          total: 0,
          uploaded: 0,
          currentFile: null
        });

        if (spinnerShown) {
          hideSpinner();
          spinnerShown = false;
        }

        alert(t("Save failed. Please try again."), ALERT_LEVEL.DANGER);
        return { success: false, error: offlineError };
      }
    } finally {
      // 🔥 최종 안전장치: 어떤 경로를 거쳐도 스피너가 켜져있으면 끔
      if (spinnerShown) {
        console.log('[ReportBuilder] ⚠️ finally 블록에서 스피너 강제 종료');
        hideSpinner();
      }
    }
  }, [saveReportToServer, report, user, t]);

  const deleteReport = () => {
    if (report.id) {
      showSpinner();
      del(`/reports/${report.id}`)
        .then((resp) => {
          history.push("/reportManagement/reports");
          alert(t("Deleted successfully"), ALERT_LEVEL.SUCCESS);
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(hideSpinner);
    } else {
      setReport({
        status: "draft",
        name: "",
        items: [],
        form: {},
        organization: "",
      });
      history.push("/reportManagement/reports");
    }
  };

  const exportReport = () => {
    showSpinner();
    get(`/reports/${report.id}/xlreport`, {}, { responseType: "blob" })
      .then((resp) => {
        // saveAs(resp.data, `${report.name}.xlsx`);
        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = String(today.getMonth() + 1).padStart(2, '0'); 
        const year = String(today.getFullYear()).slice(2); 
        const formattedDate = `${year}${month}${day}`;
  
        let fileName = `${formattedDate}_${report.name}`;
        if (report.status === "submitted") {
            fileName += "_submitted.xlsx";
        } else if (report.status === "draft") {
          fileName += "_draft.xlsx";
        } else {
          fileName += "_all.xlsx";
        }
        saveAs(resp.data, fileName);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(hideSpinner);
  };

  const validateReport = () => {
    let isValid = true;
    report.items.forEach((section) => {
      section.items.forEach((field) => {
        if (field.config.required && !field.answer) isValid = false;
      });
    });
    return isValid;
  };
  const validateRequired = () => {
    alert(t("Please fill all fields marked as '*'"), ALERT_LEVEL.DANGER);
    report.items.forEach((section, sectionidx) => {
      section.items.forEach((field, fieldidx) => {
        let tempText = '';
        if (field.config.required && !field.answer) {
          tempText = 'ez-required' + sectionidx + fieldidx;
          document.getElementById(tempText).scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" })
        }
      });
    });

  };
  useEffect(() => {
    showSpinner();
    get("/businessCategories")
      .then((resp) => {
        if (resp && resp.data) setBusinessCategories(resp.data);
        else setBusinessCategories([]);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(hideSpinner);
  }, [get, user.role.code]);

  useEffect(() => {
    if (selectedBusinessCategory) {
      showSpinner();
      get("/formCategories", { businessCategory: selectedBusinessCategory })
        .then((resp) => {
          setSelectedFormCategory();
          if (resp && resp.data) setFormCategories(resp.data);
          else setFormCategories([]);
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(hideSpinner);
    } else {
      setFormCategories([]);
      setSelectedFormCategory();
    }
  }, [selectedBusinessCategory, get, user.role.code]);

  useEffect(() => {
    if (selectedFormCategory) {
      showSpinner();
      get(`/forms`, { formCategory: selectedFormCategory })
        .then((resp) => {
          setSelectedForm();
          if (resp && resp.data && resp.data.length > 0) {
            setForms(resp.data);
          } else {
            setForms([]);
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(hideSpinner);
    } else {
      setForms([]);
      setSelectedForm();
    }
  }, [selectedFormCategory, get, user.role.code]);

  useEffect(() => {
    if (selectedForm) {
      showSpinner();
      get(`/forms/` + selectedForm)
        .then((resp) => {
          if (resp && resp.data) {
            setReport((report) => {
              return { ...report, form: resp.data.id, items: resp.data.items, name: resp.data.name };
            });
          } else {
            setReport({
              status: "draft",
              name: "",
              items: [],
              form: {},
              organization: "",
            });
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(hideSpinner);
    } else {
      setReport({
        status: "draft",
        name: "",
        items: [],
        form: {},
        organization: "",
      });
    }
  }, [selectedForm, get, user, user.role.code]);

  useEffect(() => {
    if (user.role.code === "SU") {
      showSpinner();
      get("/organizations")
        .then((resp) => {
          if (resp && resp.status === 200) {
            setOrganizations(resp.data);
          }
        })
        .catch((err) => {
          alert(t("Some error has occurred!"), ALERT_LEVEL.DANGER);
        })
        .finally(() => {
          hideSpinner();
        });
    }
  }, [user, get, t, user.role.code]);

  useEffect(() => {
    if (history.location.state && history.location.state.report) {
      const historyStateReport = history.location.state.report;
      setReport({
        ...historyStateReport,
        organization: historyStateReport.organization.id,
        createdBy: historyStateReport.createdBy.id,
      });
    }
  }, [history]);

  // 자동저장 기능 제거됨 - 중복 저장 방지를 위해 수동 저장만 사용
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     if (
  //       //autosave값이 Y일 경우에만 실행됨
  //       user.autosave === "Y" &&
  //       // (user.role.code === "OP") &&
  //       report && report.name && !(report.readOnly || report.status !== "draft")) saveReport();
  //   }, 60000);
  //   return () => clearInterval(interval);
  // }, [saveReport, report]);

  // 네트워크 모니터링 초기화
  useEffect(() => {
    const handleNetworkChange = (state) => {
      setNetworkState({
        isOnline: state.isOnline,
        shouldUseOfflineMode: state.shouldUseOfflineMode,
        networkQuality: getNetworkQualityText()
      });

      // 네트워크 상태 변화 로깅 (개발 환경에서만)
      if (process.env.NODE_ENV === 'development') {
        console.log('[ReportBuilder] 네트워크 상태 변화:', state);
      }
    };

    // 🆕 백그라운드 동기화 이벤트 핸들러
    const handleSyncEvent = (event, data) => {
      if (event === 'report_synced' && data.success && data.oldReportId && data.newReportId) {
        // 🔥 현재 편집 중인 레포트가 백그라운드로 동기화된 경우
        if (report.id === data.oldReportId) {
          console.log('[ReportBuilder] 🔄 백그라운드 동기화 완료 - ID 업데이트:', {
            old: data.oldReportId,
            new: data.newReportId
          });

          // State를 서버 ID로 업데이트 (중복 저장 방지)
          setReport(prev => ({ ...prev, id: data.newReportId }));
        }
      }
    };

    // Hot Reload 중복 방지 체크
    if (window.REPORTBUILDER_NETWORK_LISTENER) {
      console.log('[ReportBuilder] 네트워크 리스너 이미 등록됨, 스킵');
      return;
    }

    // 네트워크 리스너 등록
    addNetworkListener(handleNetworkChange);
    window.REPORTBUILDER_NETWORK_LISTENER = handleNetworkChange;

    // 🆕 동기화 리스너 등록
    addSyncListener(handleSyncEvent);

    // 초기 네트워크 상태 설정
    handleNetworkChange(networkMonitor.getNetworkState());

    return () => {
      removeNetworkListener(handleNetworkChange);
      removeSyncListener(handleSyncEvent); // 🆕 동기화 리스너 제거
      window.REPORTBUILDER_NETWORK_LISTENER = null;
    };
  }, [report.id]); // 🔥 report.id가 변경될 때마다 리스너 재등록


  return (
    <div>
      {/* 오프라인 상태 알림 */}
      {!networkState.isOnline && (
        <div className="alert alert-warning mb-3" role="alert">
          <strong>📡 {t("Offline Mode")}</strong> - {t("Network connection lost. Report will be saved locally and synced when connection is restored.")}
        </div>
      )}

      {/* 오프라인 저장 알림 */}
      {offlineNotification && (
        <div className={`alert ${
          offlineNotification.type === 'saved' ? 'alert-info' :
          offlineNotification.type === 'timeout' ? 'alert-warning' :
          'alert-warning'
        } mb-3`} role="alert">
          <strong>💾 {offlineNotification.message}</strong> - {offlineNotification.timestamp.toLocaleTimeString()}
        </div>
      )}

      {/* 전체 화면 업로드 진행률 오버레이 */}
      <UploadProgressOverlay uploadProgress={uploadProgress} />

      {/* 오프라인 상태 표시 (고정 위치) */}
      <OfflineIndicator />
      {!report.id ? (
        <div className="card">
          <div className="card-header">
            <h3>{t("Choose form")}</h3>
          </div>
          <div className="card-body">
            
            <div className="row">
              <div className="form-group col-sm-12 col-md-4">
                <label className="form-control-label">{t("Business category")}</label>
                <select
                  className="form-control"
                  placeholder={t("Business category")}
                  value={selectedBusinessCategory}
                  onChange={(e) => {
                    setSelectedBusinessCategory(e.target.value);
                    const organization = businessCategories.find((busCat) => {
                      return busCat.id === e.target.value;
                    })?.organization;
                    setReport((report) => {
                      return { ...report, organization: organization?.id };
                    });
                  }}
                >
                  <option value={t("")}>{t("Select one")}</option>
                  {businessCategories.map((businessCategory) => {
                    return (
                      <option value={businessCategory.id} key={businessCategory.id}>
                        {businessCategory.name}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="form-group col-sm-12 col-md-4">
                <label className="form-control-label">{t("Form category")}</label>
                <select
                  className="form-control"
                  placeholder={t("Form category")}
                  value={selectedFormCategory}
                  onChange={(e) => {
                    setSelectedFormCategory(e.target.value);
                  }}
                >
                  <option value={t("")}>{t("Select one")}</option>
                  {formCategories.map((formCategory) => {
                    return (
                      <option value={formCategory.id} key={formCategory.id}>
                        {formCategory.name}
                      </option>
                    );
                  })}
                </select>
              </div>
              {/* <div className="form-group col-sm-12 col-md-4">
                <label className="form-control-label">{t("Form")}</label>
                <select
                  className="form-control"
                  placeholder={t("Form")}
                  value={selectedForm}
                  onChange={(e) => {
                    setSelectedForm(e.target.value);
                  }}
                >
                  <option value={t("")}>{t("Select one")}</option>
                  {forms.map((form) => {
                    return (
                      <option value={form.id} key={form.id}>
                        {form.name}
                      </option>
                    );
                  })}
                </select>
              </div> */}
            </div>
            {user.role.code === "SU" ? (
              <div className="row">
                <div className="col-sm-12 col-md-4">
                  <label className="form-control-label">{t("Organization")}</label>
                  <select
                    className="form-control"
                    placeholder={t("Organization")}
                    value={report.organization}
                    disabled
                    onChange={(e) => {
                      setReport((form) => {
                        return { ...form, organization: e.target.value };
                      });
                    }}
                  >
                    <option value={t("")}>{t("Select Business Category")}</option>
                    {organizations.map((organization) => {
                      return (
                        <option value={organization.id} key={organization.id}>
                          {organization.name}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            ) : (
              ""
            )}
          </div>
          {selectedBusinessCategory && selectedFormCategory && forms && forms.length ? (
            <>
              {/* 20221229KSW */}

              {report.form && report.items && report.items.length > 0 ?
                <div className="ez-wook">
                  <span></span>
                </div>
                : ""}
              <div className="row">
                <div className="col-12">
                  <div className="table-responsive">
                    <table className="table align-items-center">
                      <thead className="thead-light">
                        <tr>
                          <th>{t("Form")}</th>
                          <th>{t("Business category")}</th>
                          <th>{t("Form category")}</th>
                        </tr>
                      </thead>
                      <tbody className="list">
                        {forms.map((form) => {
                          return (
                            <tr key={form.id}>
                              <td>
                                <ul className="nav nav-pills">
                                  <li
                                    className="nav-item"
                                    onClick={() => {
                                      setSelectedForm(form.id);
                                    }}
                                  >
                                    <span className={"nav-link " + (selectedForm === form.id ? "active" : "")}>
                                      <span className="nav-link-text">{form.name}</span>
                                    </span>
                                  </li>
                                </ul>
                              </td>
                              <td>
                                {
                                  businessCategories.find((businessCat) => {
                                    return businessCat.id === selectedBusinessCategory;
                                  }).name
                                }
                              </td>
                              <td>
                                {
                                  formCategories.find((formCat) => {
                                    return formCat.id === selectedFormCategory;
                                  })?.name
                                }
                              </td>

                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          ) : (
            ""
          )}
        </div>
      ) : (
        ""
      )}

      {report.form && report.items && report.items.length > 0 ? (
        <div className="card">
          <div className="card-body">
            <>
              <div className="row mb-2 mt-4">
                <div className="col-12 mb-2">
                  <div className={`${styles["actionBtns"]}`}>
                    {/* 20230131KSW -마츠나미상 요청으로 가림 */}
                    {/* {user.role.code === "MA" && report.status === "submitted" ? (
                      <>
                        <button
                          className="btn btn-success"
                          onClick={() => {
                            report.status = "approved";
                            report.verifiedBy = user.id;
                            setReport({ ...report });
                            saveReport();
                          }}
                        >
                          {t("Approve")}
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={() => {
                            report.status = "denied";
                            report.verifiedBy = user.id;
                            setReport({ ...report });
                            saveReport();
                          }}
                        >
                          {t("Deny")}
                        </button>
                      </>
                    ) : (
                      ""
                    )} */}
                    {report.status === "draft" ? (
                      !report.readOnly ? (
                        <>

                          <button
                            className="btn btn-secondary m-2"
                            onClick={() => {
                              report.status = "draft";
                              setReport({ ...report });
                              saveReport();
                            }}
                          >
                            {t("Save")}
                          </button>
                          <button
                            className="btn btn-success m-2"
                            onClick={() => {
                              if (validateReport()) {
                                report.status = "submitted";
                                setReport({ ...report });
                                saveReport();
                              } else {
                                validateRequired();
                              }
                            }}
                          >
                            {t("Submit")}
                          </button>
                          <button
                            className="btn btn-danger m-2"
                            hidden={true}
                            onClick={() => {
                              deleteReport();
                            }}
                          >
                            {t("Delete")}
                          </button>
                        </>
                      ) : (
                        ""
                      )
                    ) : ""}
                    <button
                        className="btn btn-primary  d-sm-block d-none"
                        onClick={() => {
                          exportReport();
                        }}
                      >
                        {t("Export")}
                      </button>
                  </div>
                </div>
              </div>
              <h1 className="d-flex justify-content-center mb-3">{report.name.toUpperCase()}</h1>
              <div className="row d-flex justify-content-center">
                <div className="col-sm-12 col-md-8">
                  {report.items?.map((section, idx) => {
                    return (
                      <Section key={idx} section={section} report={report} setReport={setReport} sectionIdx={idx} saveReport={saveReport} />
                    );
                  })}
                </div>
              </div>
            </>
          </div>
          <div className="card-footer">
            <div className="row mb-2 mt-4">
              <div className="col-12 mb-2">
                <div className={`${styles["actionBtns"]}`}>
                  {/* 20230131KSW 마츠나미상 요청으로 가림 */}
                  {/* {user.role.code === "MA" && report.status === "submitted" ? (
                    <>
                      <button
                        className="btn btn-success"
                        onClick={() => {
                          report.status = "approved";
                          report.verifiedBy = user.id;
                          setReport({ ...report });
                          saveReport();
                        }}
                      >
                        {t("Approve")}
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => {
                          report.status = "denied";
                          report.verifiedBy = user.id;
                          setReport({ ...report });
                          saveReport();
                        }}
                      >
                        {t("Deny")}
                      </button>
                    </>
                  ) : (
                    ""
                  )} */}
                  {report.status === "draft" ? (
                    !report.readOnly ? (
                      <>
                        <button
                          className="btn btn-secondary m-2"
                          onClick={() => {
                            report.status = "draft";
                            setReport({ ...report });
                            saveReport();
                          }}
                        >
                          {t("Save")}
                        </button>
                        <button
                          className="btn btn-success m-2"
                          onClick={() => {
                            if (validateReport()) {
                              report.status = "submitted";
                              setReport({ ...report });
                              saveReport();
                            } else {
                              validateRequired()
                              // alert(t("Please fill all fields marked as '*'"), ALERT_LEVEL.DANGER);
                            }
                          }}
                        >
                          {t("Submit")}
                        </button>
                        <button
                          className="btn btn-danger m-2"
                          hidden={true}
                          onClick={() => {
                            deleteReport();
                          }}
                        >
                          {t("Delete")}
                        </button>
                      </>
                    ) : (
                      ""
                    )
                  ) : (
                    ""
                  )}
                  <button
                      className="btn btn-primary d-sm-block d-none"
                      onClick={() => {
                        exportReport();
                      }}
                    >
                      {t("Export")}
                    </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default ReportBuilder;
